var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { iconButton } from './shared';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import './vitesse-logo';
import './vitesse-icon';
import './vitesse-notifications';
import './theme-toggle';
import './language-switcher';
import './vitesse-dropdown';
import { surfaceLinks } from '../branding';
/*
 * <vitesse-header> — the global top bar, ported from the agent's GlobalHeader.
 * 64px, var(--bg-primary), 1px bottom border. Logo (left), search (center),
 * actions (right: notifications, language, theme toggle, AI, org/user).
 *
 * THE shared header — authoritative copy lifted from app-ui (the gold
 * standard), decoupled from any app's concrete notification store via
 * `NotificationSource` (the app passes its manager, which satisfies it
 * structurally). The account menu and org switcher are mounted in the LIGHT DOM
 * (by the app) and projected here via <slot name="user"|"org">, so a widget whose
 * CSS lands in document.head still reaches them — shadow DOM would not.
 */
let VitesseHeader = class VitesseHeader extends LitElement {
    constructor() {
        super();
        /** Whether the AI rail is open. Drives the button's live state — see `.ai-button.is-open`. */
        this.aiOpen = false;
        /** Offer the marketplace toggle. Off by default — a header button an app does
         *  not handle is a button that does nothing, which is worse than absence. */
        this.marketplace = false;
        this.marketplaceOpen = false;
        /** The assistant is working RIGHT NOW. Shown so the rail can be closed for room without losing
         *  track of it — the whole point of collapsing a panel that keeps running. */
        this.aiBusy = false;
        /** It finished while you were not looking. Cleared by opening the rail. */
        this.aiDone = false;
        new LocaleController(this); // re-render this header whenever the language changes
    }
    static { this.styles = [
        iconButton,
        css `
      :host {
        display: block;
        flex-shrink: 0;
      }
      .header {
        height: 64px;
        display: flex;
        align-items: center;
        gap: 24px;
        padding: 0 24px;
        background: var(--bg-primary);
        border-bottom: 1px solid var(--border-color);
        position: relative;
        z-index: 100;
      }
      .left {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 12px;
      }
      /* The surface switcher wears the base-control radius (rectangular), not the dropdown's
         default rounded pill, and is slimmed to sit level with the logo wordmark and the
         tree-toggle glyph beside it — all via custom properties that inherit into the
         dropdown's shadow, so no other caller is affected. */
      .surface-switch {
        --dropdown-radius: 6px;
        --dropdown-height: 22px;
        --dropdown-pad-x: 8px;
        --dropdown-font: 11.5px;
      }
      .logo-button {
        background: transparent;
        border: none;
        padding: 0;
        cursor: pointer;
        display: inline-flex;
        color: var(--text-primary);
        transition: opacity 0.2s ease;
      }
      /* The logo darkens itself on hover (brightness filter); don't also fade the
         button, which would lighten it. */
      /* Optically center the wordmark against the tree-toggle glyph (a hair down
         reads better than mathematically centered). */
      .logo-button vitesse-logo {
        transform: translateY(0.5px);
      }
      .center {
        flex: 1;
        max-width: 600px;
        display: flex;
        justify-content: center;
      }
      .search {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px 16px;
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: 8px;
        color: var(--text-secondary);
        font-size: 14px;
        cursor: pointer;
        transition: border-color 0.2s ease;
      }
      .search:hover {
        border-color: var(--border-color-light);
      }
      .search .placeholder {
        flex: 1;
        text-align: left;
      }
      .kbd {
        display: flex;
        align-items: center;
        gap: 2px;
        padding: 2px 6px;
        background: var(--bg-tertiary);
        border: 1px solid var(--border-color);
        border-radius: 4px;
        font-size: 12px;
        font-family: monospace;
        color: var(--text-tertiary);
      }
      .actions {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
      }
      /* The AI toggle is always the AI pink (--ai) — everything AI is purple,
         visually integrated + mentally unique. Stays pink on hover (the
         icon-button blue is overridden); it still grows like any icon button. */
      /* CLOSED: the brand pink, sitting quietly with the other header actions. */
      .ai-button {
        padding: 6px 8px;
        color: var(--ai);
        position: relative;
      }
      /*
       * THE STATUS LINE, under the mark.
       *
       * A blinking dot was wrong twice over: a dot is a notification's shape, and blinking is how
       * something demands to be looked at. This is neither — the panel is shut because you wanted
       * the room, and the signal exists so you can IGNORE it until it changes.
       *
       * So: a short segment of underline that travels from one side to the other and back, on the
       * same ease-in-out the app's disclosures use. Motion carries "still going" without asking for
       * attention, and a line under a word is quieter than anything drawn beside it.
       */
      .ai-line {
        position: absolute;
        left: 6px;
        right: 6px;
        bottom: 3px;
        height: 2px;
        border-radius: 1px;
        overflow: hidden;
        pointer-events: none;
      }
      .ai-line::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 38%;
        border-radius: 1px;
      }
      /*
       * WORKING WEARS THE MARK'S OWN COLOURS; DONE IS THE APP'S BLUE.
       *
       * It was pink into red, and red is the one thing this line must not be: red is what every
       * destructive and every failing control in this product wears, so a working assistant read as
       * an assistant in trouble. The gradient it wears instead is the SPARKLE'S — the same teal →
       * magenta → amber the AI mark beside it is stroked with — so the line and the icon above it
       * are visibly one thing, saying the same thing.
       *
       * The stops are literals here for the same reason they are literals in the icon: the icon's
       * are an SVG paint server, which cannot read a custom property, and a token on this side only
       * would be the drift the shared spelling exists to avoid. If the mark's colours change, these
       * follow in the same edit.
       */
      .ai-line.busy::after {
        background: linear-gradient(90deg, #2fd4de, #d65cf0, #ffab3d);
        animation: ai-line-shuttle 1.15s cubic-bezier(0.65, 0, 0.35, 1) infinite alternate;
      }
      /* Finished, and not yet looked at: the line rests, filling the width. Still a line, so the
         button never changes shape — only what it is doing. */
      .ai-line.done::after {
        width: 100%;
        background: linear-gradient(90deg, var(--brand-blue-light, #5cb0ec), var(--brand-blue, #2491db), var(--brand-blue-dark, #1652c9));
      }
      @keyframes ai-line-shuttle {
        from {
          transform: translateX(0);
        }
        to {
          transform: translateX(163%);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .ai-line.busy::after {
          animation: none;
          width: 100%;
          opacity: 0.6;
        }
      }
      .ai-button:hover:not(:disabled) {
        color: var(--ai);
      }
      /*
       * OPEN: the mark takes the app's GATEWAY GRADIENT.
       *
       * The same teal → purple → orange wash that marks which gateway a card belongs to, reused here
       * so "the AI surface is live" is said in a colour language the app already speaks — rather than
       * inventing a motion effect that exists nowhere else.
       *
       * The icon strokes it via <vitesse-icon gradient> (an SVG stroke needs a paint server, so the
       * gradient is defined inside the icon's own shadow root). The label clips the same gradient to
       * its glyphs. One idea, two techniques, because SVG stroke and text fill have no shared one.
       *
       * CLOSED is the muted wash, OPEN is saturated and rotating — the button keeps its identity and
       * changes only intensity, which is what makes the state readable at a glance without becoming
       * a different control.
       */
      .ai-label {
        background: var(--gateway-gradient-soft-text, linear-gradient(to right, #7fd4d9, #c9a3dd, #f0c48f));
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
      }
      .ai-button.is-open .ai-label {
        background: var(--gateway-gradient);
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
      }
      .ai-label {
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.02em;
      }
      ::slotted([slot='user']),
      ::slotted([slot='org']) {
        display: flex;
        align-items: center;
      }
    `,
    ]; }
    /** The App / AI / Fleet switcher — a text-pill dropdown beside the logo that navigates, in the
     *  SAME tab, to the sibling product surface. Links are derived from the current URL (see
     *  branding.surfaceLinks), so it needs no per-app config and stays correct in dev and prod. */
    renderSurfaceSwitcher() {
        const links = surfaceLinks(this.surface);
        const cur = links.find((l) => l.current) ?? links[0];
        const label = (id) => id === 'ai' ? t('header.surfaceAi') : id === 'fleet' ? t('header.surfaceFleet') : t('header.surfaceApp');
        return html `<vitesse-dropdown
      class="surface-switch"
      align="left"
      .triggerIcon=${''}
      .triggerLabel=${label(cur.id)}
      .triggerTitle=${t('header.switchSurface')}
      .value=${cur.href}
      .items=${links.map((l) => ({ value: l.href, label: label(l.id) }))}
      @dropdown-select=${(e) => {
            // Same tab, real navigation — the surfaces are separate origins, so this is a load, not a route.
            if (e.detail.value && e.detail.value !== cur.href)
                window.location.assign(e.detail.value);
        }}
    ></vitesse-dropdown>`;
    }
    render() {
        return html `
      <header class="header">
        <div class="left">
          <button
            class="icon-btn tree-toggle"
            title=${t('header.toggleTree')}
            @click=${() => this.dispatchEvent(new CustomEvent('vitesse-toggle-tree', { bubbles: true, composed: true }))}
          >
            <vitesse-icon name="list-tree" size="20"></vitesse-icon>
          </button>
          <button
            class="logo-button"
            title=${t('header.home')}
            @click=${() => this.dispatchEvent(new CustomEvent('vitesse-home', { bubbles: true, composed: true }))}
          >
            <vitesse-logo .textOnly=${true}></vitesse-logo>
          </button>
          ${this.renderSurfaceSwitcher()}
        </div>
        <div class="center">
          <button class="search">
            <vitesse-icon name="search" size="18"></vitesse-icon>
            <span class="placeholder">${t('header.search')}</span>
            <span class="kbd"><span>⌘</span>K</span>
          </button>
        </div>
        <div class="actions">
          <vitesse-notifications .manager=${this.notifications}></vitesse-notifications>
          <vitesse-language-switcher></vitesse-language-switcher>
          <vitesse-theme-toggle></vitesse-theme-toggle>
          ${this.marketplace
            ? html `<button
                  class="icon-btn lg ${this.marketplaceOpen ? 'active' : ''}"
                  title=${this.marketplaceOpen ? t('header.closeMarketplace') : t('header.toggleMarketplace')}
                  @click=${() => this.dispatchEvent(new CustomEvent('vitesse-toggle-marketplace', { bubbles: true, composed: true }))}
                >
                  <vitesse-icon name="store" size="18"></vitesse-icon>
                </button>`
            : nothing}
          <button
            class="icon-btn ai-button ${this.aiOpen ? 'is-open' : ''}"
            title=${t('header.toggleAi')}
            @click=${() => this.dispatchEvent(new CustomEvent('vitesse-toggle-ai', { bubbles: true, composed: true }))}
          >
            <!-- The mark ALWAYS wears the gradient; opening the rail turns it up and sets it
                 turning. Same idea either way, so the button never changes identity — only
                 intensity. -->
            <vitesse-icon name="sparkles" size="18" gradient ?gradientActive=${this.aiOpen}></vitesse-icon>
            <span class="ai-label">${t('header.aiShort')}</span>
            <!--
              TWO STATES, ONE LINE. Working shuttles; finished-while-you-were-away rests full-width.
              Nothing at all the rest of the time — a permanent indicator is not an indicator.
            -->
            ${this.aiBusy
            ? html `<span class="ai-line busy" title=${t('header.aiWorking')}></span>`
            : this.aiDone
                ? html `<span class="ai-line done" title=${t('header.aiFinished')}></span>`
                : nothing}
          </button>
          <slot name="org"></slot>
          <slot name="user"></slot>
        </div>
      </header>
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseHeader.prototype, "notifications", void 0);
__decorate([
    property({ type: Boolean })
], VitesseHeader.prototype, "aiOpen", void 0);
__decorate([
    property({ type: Boolean })
], VitesseHeader.prototype, "marketplace", void 0);
__decorate([
    property({ type: Boolean })
], VitesseHeader.prototype, "marketplaceOpen", void 0);
__decorate([
    property({ type: Boolean })
], VitesseHeader.prototype, "aiBusy", void 0);
__decorate([
    property({ type: Boolean })
], VitesseHeader.prototype, "aiDone", void 0);
__decorate([
    property()
], VitesseHeader.prototype, "surface", void 0);
VitesseHeader = __decorate([
    customElement('vitesse-header')
], VitesseHeader);
export { VitesseHeader };
//# sourceMappingURL=vitesse-header.js.map